/**
 * Pilot Program Sign-up Page Component
 * Sign-up form for trainers and institutes to join the HeatCraftHP pilot program
 * @module Contact
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Typography, Form, Input, Button, Card, message, Row, Col } from 'antd';
import { MailOutlined, UserOutlined, PhoneOutlined, EnvironmentOutlined, LinkedinOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PagesWrapper from '../../PagesWrapper';
import api from '../../services/apiClient';
import styles from './Contact.module.css';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

/**
 * Pilot Program Page - Sign-up form for trainers and institutes
 */
const Contact = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  /**
   * Handle form submission
   * @param {Object} values - Form values (name, email, subject, message)
   */
  const handleSubmit = async (values) => {
    setLoading(true);
    
    try {
      await api.post('/contact', values, { suppressNotification: true });
      message.success(t('contact.success'));
      form.resetFields();
      
    } catch (error) {
      message.error(t('contact.error'));
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  };



  return (
    <PagesWrapper>
      <div className={styles.contactContainer}>
        
        {/* Hero Section */}
        <section className={styles.heroSection}>
          <div className={styles.heroContent}>
            <Title level={1} className={styles.heroTitle}>
              {t('contact.heroTitle')}
            </Title>
            <Paragraph className={styles.heroSubtitle}>
              {t('contact.heroSubtitle')}
            </Paragraph>
          </div>
        </section>

        {/* Contact Info Section */}
        <section className={styles.contactInfo}>
          <div className={styles.contactInfoContent}>
            <a 
              href="https://www.google.com/maps/search/?api=1&query=Augustinerbach+6,+52062+Aachen,+Germany" 
              target="_blank" 
              rel="noopener noreferrer" 
              className={styles.impressumItemLink}
            >
              <div className={styles.impressumItem}>
                <EnvironmentOutlined className={styles.impressumIcon} />
                <div className={styles.impressumDetails}>
                  <Text strong className={styles.impressumLabel}>Institut für Wärme- und Stoffübertragung</Text>
                  <Text className={styles.impressumText}>
                    Augustinerbach 6, 52062 Aachen, Germany
                  </Text>
                </div>
              </div>
            </a>
            
            <div className={styles.impressumItem}>
              <MailOutlined className={styles.impressumIcon} />
              <div className={styles.impressumDetails}>
                <a href="mailto:info@wsa.rwth-aachen.de" className={styles.impressumLink}>
                  info@wsa.rwth-aachen.de
                </a>
              </div>
            </div>
            
            <div className={styles.impressumItem}>
              <PhoneOutlined className={styles.impressumIcon} />
              <div className={styles.impressumDetails}>
                <a href="tel:+4924180-93634" className={styles.impressumLink}>
                  +49 241 80-93634
                </a>
              </div>
            </div>
            
            <a 
              href="https://www.linkedin.com/company/heatcrafthp-project/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className={styles.impressumItemLink}
            >
              <div className={styles.impressumItem}>
                <LinkedinOutlined className={styles.impressumIcon} />
                <div className={styles.impressumDetails}>
                  <span className={styles.impressumLink}>
                    HeatCraftHP Project
                  </span>
                </div>
              </div>
            </a>
          </div>
        </section>

        {/* Sign-up Form Section */}
        <section className={styles.formSection}>
          <div className={styles.formWrapper}>
            <div className={styles.registrationCallout}>
              <div className={styles.registrationCalloutContent}>
                <Text strong className={styles.registrationCalloutTitle}>
                  {t('auth.createYourAccount')}
                </Text>
                <Text className={styles.registrationCalloutText}>
                  {t('auth.approvalProcessInfo')}
                </Text>
              </div>
              <Link to="/register" className={styles.registrationCalloutLink}>
                <Button size="large" className={styles.registrationCalloutButton}>
                  {t('auth.submitRegistration')}
                </Button>
              </Link>
            </div>

            <Card className={styles.formCard}>
              <Title level={2} className={styles.formTitle}>
                {t('contact.formTitle')}
              </Title>
              <Paragraph className={styles.formDescription}>
                {t('contact.formDescription')}
              </Paragraph>

              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                className={styles.contactForm}
                requiredMark="optional"
              >
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="name"
                      label={t('contact.name')}
                      rules={[
                        { required: true, message: t('contact.nameRequired') },
                        { min: 2, message: t('contact.nameMin') }
                      ]}
                    >
                      <Input
                        prefix={<UserOutlined />}
                        placeholder={t('contact.namePlaceholder')}
                        size="large"
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      name="email"
                      label={t('contact.email')}
                      rules={[
                        { required: true, message: t('contact.emailRequired') },
                        { type: 'email', message: t('contact.emailInvalid') }
                      ]}
                    >
                      <Input
                        prefix={<MailOutlined />}
                        placeholder={t('contact.emailPlaceholder')}
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="subject"
                  label={t('contact.subject')}
                  rules={[
                    { required: true, message: t('contact.subjectRequired') },
                    { min: 5, message: t('contact.subjectMin') }
                  ]}
                >
                  <Input
                    placeholder={t('contact.subjectPlaceholder')}
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="message"
                  label={t('contact.message')}
                  rules={[
                    { required: true, message: t('contact.messageRequired') },
                    { min: 10, message: t('contact.messageMin') }
                  ]}
                >
                  <TextArea
                    rows={6}
                    placeholder={t('contact.messagePlaceholder')}
                    maxLength={1000}
                    showCount
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={loading}
                    block
                    className={styles.submitButton}
                  >
                    {t('contact.send')}
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </div>
        </section>

      </div>
    </PagesWrapper>
  );
};

export default Contact;
